import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import { CancelButton } from 'src/components/Buttons';
import { CurrencyFilterSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { DnDFileUploader } from 'src/core/POS/DnDFileUploader';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKET_PLACE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { downloadFileFromBlob } from 'src/utils/fileUtils';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Marketplace,
  MarketplacePaymentClient,
  UiMoney,
} from 'src/WebApiController';

// Define the form interface
export interface MarketplacePaymentUploadForm {
  marketplace: Marketplace;
  externalPaymentId: string;
  paymentReferenceId?: string;
  paymentDate: string;
  paymentAmount: UiMoney;
  currency: string;
}

export type BulkUploadMarketplacePaymentLineDialogProps = ComponentProps<
  typeof RSModal
> & {
  onOkay: (
    marketplacePaymentForm: MarketplacePaymentUploadForm,
    file: File
  ) => Promise<void | null>;
  onCancel: () => void;
};

export const BulkUploadMarketplacePaymentLineDialog = ({
  ...rest
}: BulkUploadMarketplacePaymentLineDialogProps) => {
  const methods = useForm<MarketplacePaymentUploadForm>();

  return (
    <FormProvider {...methods}>
      <BulkUploadMarketplacePaymentLineDialogContent {...rest} {...methods} />
    </FormProvider>
  );
};

function BulkUploadMarketplacePaymentLineDialogContent({
  onOkay,
  onCancel,
  handleSubmit,
  watch,
  reset,
  control,
  formState,
  setValue,
  getValues,
  register,
  ...rest
}: BulkUploadMarketplacePaymentLineDialogProps &
  Omit<
    ComponentProps<typeof FormProvider<MarketplacePaymentUploadForm, unknown>>,
    'children'
  >) {
  const { loginContext, activeAccountWebClientConfig } = useAppContext();
  const requiredText = useContent(ContentId.Required);
  const { setActivePosEntity } = useActivePosEntityContext();
  const { getUiCurrency } = useLocalizationContext();
  const { showErrorDialog } = useClientLogContext();

  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();

  const marketplace = watch('marketplace');
  const externalPaymentId = watch('externalPaymentId');
  const paymentDate = watch('paymentDate');
  const paymentAmount = watch('paymentAmount');
  const currency = watch('currency');

  const marketplacePaymentAmountMustBeGreaterThanZero = useContent(
    ContentId.MarketplacePaymentAmountMustBeGreaterThanZero
  );

  const { isValid: isFormValid } = formState;

  const onDownloadTemplateClick = useCallback(() => {
    setIsLoading(true);
    tryInvokeApi(
      async () => {
        const file = await new MarketplacePaymentClient(
          activeAccountWebClientConfig
        ).downloadMarketplacePaymentTemplate();

        if (file) {
          downloadFileFromBlob(
            file.fileName ?? 'marketplace-payment-line-template.csv',
            file.data
          );
        }
      },
      (error) => {
        showErrorDialog(
          'MarketplacePaymentClient.downloadMarketplacePaymentLineTemplate',
          error
        );
      },
      () => setIsLoading(false)
    );
  }, [activeAccountWebClientConfig, showErrorDialog]);

  const onSubmitHandler = useCallback(() => {
    if (!uploadedFile) {
      showErrorDialog('MarketplacePaymentUpload', {
        message: 'Please select a file to upload',
      });
      return;
    }

    setIsLoading(true);
    handleSubmit(async (formValues) => {
      try {
        await onOkay(formValues, uploadedFile);
      } catch (error) {
        // Error handling is done in the onOkay function
      } finally {
        setIsLoading(false);
      }
    })();
  }, [handleSubmit, onOkay, uploadedFile, showErrorDialog]);

  const isInputValid = useMemo(() => {
    return (
      marketplace &&
      externalPaymentId &&
      paymentDate &&
      paymentAmount &&
      uploadedFile &&
      currency &&
      isFormValid
    );
  }, [
    marketplace,
    externalPaymentId,
    paymentDate,
    paymentAmount,
    uploadedFile,
    isFormValid,
    currency,
  ]);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <GenericDialog
      {...rest}
      size="md"
      header={
        <div className="dialog-header">
          <Content id={ContentId.UploadMarketplacePayment} />
        </div>
      }
      onOpened={() => {
        reset();
      }}
      onClosed={() => {
        reset();
        setUploadedFile(undefined);
      }}
      onKeyUp={(e) => {
        if (submitButtonRef.current && e.key === 'Enter' && isInputValid) {
          submitButtonRef.current.click();
        }
      }}
      footer={
        <Stack direction="row" gap="s">
          <CancelButton
            onClick={onCancel}
            disabled={isLoading}
            textContentId={ContentId.Cancel}
          />
          <Button
            onClick={onSubmitHandler}
            disabled={!isInputValid || isLoading}
            ref={submitButtonRef}
          >
            <Content id={ContentId.Upload} />
          </Button>
        </Stack>
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <Stack direction="column" width="full" gap="xxl">
        {/* Form Input Fields */}
        <PosFormField
          label={<Content id={ContentId.Marketplace} />}
          errors={formState.errors.marketplace?.message}
        >
          <Controller
            name="marketplace"
            rules={{ required: requiredText }}
            control={control}
            render={({ field }) => (
              <PosEnumSelect
                {...field}
                style={{ width: '100%' }}
                placeholderText={ContentId.Select}
                enableEmptySelection
                onChange={(marketplace) => {
                  if (marketplace) {
                    setValue('marketplace', marketplace);
                  }
                }}
                valueOptionsContent={(
                  loginContext?.user?.activeAccount?.marketplaceSettings?.map(
                    (m) => m.mkp
                  ) ?? [Marketplace.StubHub]
                ).reduce(
                  (cur, m) => {
                    cur[m] = MARKET_PLACE_TO_CID[m];
                    return cur;
                  },
                  {} as Record<Marketplace, string>
                )}
              />
            )}
          />
        </PosFormField>

        <PosFormField
          label={<Content id={ContentId.ExternalPaymentId} />}
          errors={formState.errors.externalPaymentId?.message}
        >
          <PosTextField
            {...register('externalPaymentId', { required: requiredText })}
          />
        </PosFormField>

        <PosFormField
          label={<Content id={ContentId.PaymentDate} />}
          errors={formState.errors.paymentDate?.message}
        >
          <Controller
            name="paymentDate"
            rules={{ required: requiredText }}
            control={control}
            render={({ field }) => (
              <DatePickerInput
                fieldError={formState.errors.paymentDate?.message}
                locale={getLocaleFromLanguageOrCurrent()}
                {...field}
                onDateChange={(d) => {
                  setValue('paymentDate', d.toISOString());
                }}
              />
            )}
          />
        </PosFormField>

        <PosFormField
          label={<Content id={ContentId.Amount} />}
          errors={formState.errors.paymentAmount?.message}
        >
          <PosTextField
            {...register('paymentAmount.amt', {
              validate: {
                required: (value: number | null) => {
                  const v = value ? String(value) : '';
                  return !v.length ? requiredText : undefined;
                },
                minValue: (value: number | null) => {
                  return value && value <= 0
                    ? marketplacePaymentAmountMustBeGreaterThanZero
                    : undefined;
                },
              },
              setValueAs(amount): number | null {
                if (!amount || isNaN(Number(amount))) {
                  return null;
                }

                return Number(amount);
              },
              onChange() {
                const amount = getValues().paymentAmount?.amt;
                if (formState.errors['paymentAmount'] || !amount) {
                  return;
                }

                const uiCurrency = getUiCurrency(currency);
                setValue('paymentAmount', {
                  currency: currency,
                  dec: uiCurrency.dec,
                  amt: Number(amount),
                  disp: formatCurrency(
                    Number(amount),
                    uiCurrency.code,
                    uiCurrency.dec
                  ),
                });
              },
            })}
            type="number"
            prefixDisplay={getUiCurrency(currency).sym}
            postfixDisplay={
              <CurrencyFilterSelector
                value={currency}
                onChange={(currencyCode: string) => {
                  setValue('currency', currencyCode, {
                    shouldValidate: true,
                  });
                }}
              />
            }
          />
        </PosFormField>

        <PosFormField
          label={<Content id={ContentId.PaymentReferenceId} />}
          errors={formState.errors.paymentReferenceId?.message}
        >
          <PosTextField {...register('paymentReferenceId')} />
        </PosFormField>

        {/* File Upload Section */}
        <Stack direction="column" gap="m">
          <Button
            variant="outline"
            onClick={onDownloadTemplateClick}
            disabled={isLoading}
          >
            <Content id={ContentId.DownloadTemplate} />
          </Button>

          <DnDFileUploader
            disabled={isLoading}
            onChange={(files) => files[0] && setUploadedFile(files[0])}
            multiple={false}
            acceptedFileTypes={['.csv']}
          />
        </Stack>
      </Stack>
    </GenericDialog>
  );
}
